import { useEffect, useRef, useState } from 'react';

const TARGET_DEFAULT = 1000;
const GRAVITY = 2200;
const JUMP_VELOCITY = -780;
const GROUND_PADDING = 18;
const PLAYER_WIDTH = 34;
const PLAYER_HEIGHT = 38;
const PLAYER_X = 48;
const BASE_SPEED = 270;
const MAX_SPEED = 470;
const JUMP_AIR_TIME = (2 * Math.abs(JUMP_VELOCITY)) / GRAVITY;
const MAX_JUMP_HEIGHT = (JUMP_VELOCITY * JUMP_VELOCITY) / (2 * GRAVITY);

const createInitialState = () => ({
  playerY: 0,
  playerVy: 0,
  grounded: true,
  obstacles: [],
  spawnTimer: 0.9,
  distance: 0,
  score: 0,
  speed: BASE_SPEED,
  anim: 0,
});

const getJumpDistance = (speed) => speed * JUMP_AIR_TIME;

const getMinGapBetweenObstacles = (speed) =>
  Math.max(180, getJumpDistance(speed) * 1.15 + PLAYER_WIDTH * 1.5);

const getRandomGap = (speed) => {
  const minGap = getMinGapBetweenObstacles(speed);
  return minGap * (1.15 + Math.random() * 1.6);
};

const getMaxObstacleWidth = (speed) => getJumpDistance(speed) * 0.38;

const getMaxObstacleHeight = () => Math.max(36, MAX_JUMP_HEIGHT - PLAYER_HEIGHT * 0.55);

const pickObstacleUnits = (score, maxWidth, unitWidth, unitGap, targetScore) => {
  const widthFor = (units) => units * unitWidth + (units - 1) * unitGap;
  const progress = Math.min(1, Math.max(0, score / Math.max(targetScore, 1)));
  const roll = Math.random();

  let units = 1;
  if (progress < 0.18) {
    units = 1;
  } else if (progress < 0.4) {
    units = roll < 0.18 + (progress - 0.18) * 0.9 ? 2 : 1;
  } else if (progress < 0.65) {
    const doubleChance = 0.28 + (progress - 0.4) * 0.7;
    const tripleChance = 0.06 + (progress - 0.4) * 0.35;
    if (roll < tripleChance) units = 3;
    else if (roll < tripleChance + doubleChance) units = 2;
  } else {
    const tripleChance = 0.16 + (progress - 0.65) * 0.35;
    const doubleChance = 0.38 + (progress - 0.65) * 0.2;
    if (roll < tripleChance) units = 3;
    else if (roll < tripleChance + doubleChance) units = 2;
  }

  while (units > 1 && widthFor(units) > maxWidth) {
    units -= 1;
  }

  return units;
};

const createObstacle = (startX, score, speed, targetScore) => {
  const progress = Math.min(1, Math.max(0, score / Math.max(targetScore, 1)));
  const tallChance = Math.max(0, Math.min(0.62, (progress - 0.12) * 0.9));
  const tall = Math.random() < tallChance;
  const unitWidth = tall ? 20 : 16;
  const unitGap = 4;
  const maxWidth = getMaxObstacleWidth(speed);
  const maxHeight = getMaxObstacleHeight();
  const units = pickObstacleUnits(score, maxWidth, unitWidth, unitGap, targetScore);
  const shortHeight = 28 + Math.random() * 8;
  const tallHeight = 42 + Math.random() * 10 + progress * 8;
  const height = Math.min(tall ? tallHeight : shortHeight, maxHeight);

  return {
    x: startX,
    units,
    unitWidth,
    gap: unitGap,
    width: units * unitWidth + (units - 1) * unitGap,
    height,
    tall,
  };
};

const drawCactusUnit = (context, x, groundY, unitWidth, height) => {
  const y = groundY - height;
  const w = unitWidth;

  context.fillStyle = '#6d3fb7';
  context.fillRect(x + w * 0.35, y, w * 0.3, height);
  context.fillRect(x, y + height * 0.28, w * 0.35, w * 0.28);
  context.fillRect(x + w * 0.65, y + height * 0.42, w * 0.35, w * 0.28);
  context.fillRect(x, y + height * 0.28, w * 0.18, height * 0.35);
  context.fillRect(x + w * 0.82, y + height * 0.42, w * 0.18, height * 0.28);

  context.fillStyle = '#9b6fe0';
  context.fillRect(x + w * 0.4, y + 4, w * 0.12, height - 8);
};

let jumpAudioContext;

const getJumpAudioContext = () => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return null;

  if (!jumpAudioContext) {
    jumpAudioContext = new AudioContext();
  }

  if (jumpAudioContext.state === 'suspended') {
    jumpAudioContext.resume().catch(() => {});
  }

  return jumpAudioContext;
};

const playJumpSound = () => {
  const context = getJumpAudioContext();
  if (!context) return;

  const now = context.currentTime;
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'square';
  oscillator.frequency.setValueAtTime(420, now);
  oscillator.frequency.exponentialRampToValueAtTime(680, now + 0.07);
  oscillator.frequency.exponentialRampToValueAtTime(320, now + 0.14);

  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.055, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(now);
  oscillator.stop(now + 0.18);
};

const tryJump = (state) => {
  if (!state.grounded) return false;
  state.grounded = false;
  state.playerVy = JUMP_VELOCITY;
  playJumpSound();
  return true;
};

function RunnerGame({
  active = true,
  locked = false,
  targetScore = TARGET_DEFAULT,
  onWin,
  onHit,
  levelKey,
}) {
  const canvasRef = useRef(null);
  const stateRef = useRef(createInitialState());
  const wonRef = useRef(false);
  const hitLockRef = useRef(false);
  const activeRef = useRef(active);
  const lockedRef = useRef(locked);
  const targetScoreRef = useRef(Number(targetScore) || TARGET_DEFAULT);
  const onWinRef = useRef(onWin);
  const onHitRef = useRef(onHit);
  const [score, setScore] = useState(0);

  activeRef.current = active;
  lockedRef.current = locked;
  targetScoreRef.current = Number(targetScore) || TARGET_DEFAULT;
  onWinRef.current = onWin;
  onHitRef.current = onHit;

  useEffect(() => {
    stateRef.current = createInitialState();
    wonRef.current = false;
    hitLockRef.current = false;
    setScore(0);
  }, [levelKey]);

  useEffect(() => {
    if (!locked) {
      hitLockRef.current = false;
    }
  }, [locked]);

  useEffect(() => {
    const jump = (event) => {
      if (!activeRef.current || lockedRef.current || wonRef.current || hitLockRef.current) return;
      if (event.code !== 'Space' && event.code !== 'ArrowUp') return;
      event.preventDefault();

      const state = stateRef.current;
      if (!state.grounded) return;
      tryJump(state);
    };

    window.addEventListener('keydown', jump);
    return () => window.removeEventListener('keydown', jump);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    let frameId = 0;
    let lastTime = performance.now();
    let scoreTick = 0;

    const getSize = () => {
      const parentWidth = canvas.parentElement?.clientWidth || 320;
      return {
        width: Math.max(280, Math.floor(parentWidth)),
        height: 210,
      };
    };

    const resize = () => {
      const { width, height } = getSize();
      const ratio = window.devicePixelRatio || 1;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    const draw = (width, height) => {
      const state = stateRef.current;
      const goal = targetScoreRef.current;
      const groundY = height - GROUND_PADDING;
      const shownScore = Number.isFinite(state.score) ? Math.floor(state.score) : 0;

      context.clearRect(0, 0, width, height);

      const sky = context.createLinearGradient(0, 0, 0, height);
      sky.addColorStop(0, '#fff7fb');
      sky.addColorStop(1, '#ffe8f4');
      context.fillStyle = sky;
      context.fillRect(0, 0, width, height);

      context.fillStyle = 'rgba(183, 222, 255, 0.55)';
      context.beginPath();
      context.moveTo(0, groundY - 42);
      context.quadraticCurveTo(width * 0.25, groundY - 78, width * 0.5, groundY - 40);
      context.quadraticCurveTo(width * 0.75, groundY - 70, width, groundY - 36);
      context.lineTo(width, groundY);
      context.lineTo(0, groundY);
      context.fill();

      context.fillStyle = '#f3b7d8';
      context.fillRect(0, groundY, width, height - groundY);
      context.fillStyle = '#ffd6ea';
      context.fillRect(0, groundY, width, 4);

      context.strokeStyle = 'rgba(109, 63, 183, 0.28)';
      context.lineWidth = 2;
      context.setLineDash([10, 14]);
      context.beginPath();
      const distance = Number.isFinite(state.distance) ? state.distance : 0;
      const dashOffset = -((distance * 0.35) % 24);
      context.moveTo(dashOffset, groundY + 10);
      context.lineTo(width + 24, groundY + 10);
      context.stroke();
      context.setLineDash([]);

      state.obstacles.forEach((obstacle) => {
        if (!Number.isFinite(obstacle.x)) return;
        for (let i = 0; i < obstacle.units; i += 1) {
          const unitX = obstacle.x + i * (obstacle.unitWidth + obstacle.gap);
          drawCactusUnit(context, unitX, groundY, obstacle.unitWidth, obstacle.height);
        }
      });

      const playerTop = groundY - PLAYER_HEIGHT - state.playerY;
      const bob = state.grounded ? Math.sin(state.anim * 14) * 2 : 0;
      const py = playerTop + bob;

      context.save();
      context.translate(PLAYER_X + PLAYER_WIDTH / 2, py + PLAYER_HEIGHT / 2);
      context.rotate(state.grounded ? Math.sin(state.anim * 14) * 0.05 : -0.12);
      context.fillStyle = '#e84d7a';
      context.beginPath();
      const hw = PLAYER_WIDTH / 2;
      const hh = PLAYER_HEIGHT / 2;
      context.moveTo(0, hh * 0.7);
      context.bezierCurveTo(-hw * 1.15, hh * 0.1, -hw * 1.05, -hh * 0.75, 0, -hh * 0.25);
      context.bezierCurveTo(hw * 1.05, -hh * 0.75, hw * 1.15, hh * 0.1, 0, hh * 0.7);
      context.fill();
      context.fillStyle = '#fff';
      context.beginPath();
      context.arc(-5, -4, 3.2, 0, Math.PI * 2);
      context.arc(5, -4, 3.2, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = '#4a3558';
      context.beginPath();
      context.arc(-5, -3.5, 1.4, 0, Math.PI * 2);
      context.arc(5, -3.5, 1.4, 0, Math.PI * 2);
      context.fill();
      context.restore();

      context.fillStyle = '#6d3fb7';
      context.font = '800 16px Nunito, system-ui, sans-serif';
      context.textAlign = 'right';
      context.fillText(`${shownScore} / ${goal}`, width - 12, 24);
    };

    const step = (now) => {
      const { width, height } = getSize();
      const dt = Math.min(Math.max((now - lastTime) / 1000, 0), 0.04);
      lastTime = now;

      const running =
        activeRef.current && !lockedRef.current && !wonRef.current && !hitLockRef.current;

      if (running) {
        const state = stateRef.current;
        const goal = targetScoreRef.current;
        state.anim += dt;

        if (!Number.isFinite(state.distance)) state.distance = 0;
        if (!Number.isFinite(state.score)) state.score = 0;

        const progress = Math.min(1, Math.max(0, state.score / goal));
        // Smooth ease-in ramp (no fractional powers that can NaN).
        const ramp = progress * progress * (0.35 + 0.65 * progress);
        state.speed = BASE_SPEED + (MAX_SPEED - BASE_SPEED) * ramp;

        state.distance += state.speed * dt;
        state.score = Math.min(goal, state.distance * 0.09);

        scoreTick += dt;
        if (scoreTick > 0.08) {
          scoreTick = 0;
          setScore(Math.floor(state.score));
        }

        if (!state.grounded) {
          state.playerVy += GRAVITY * dt;
          state.playerY += -state.playerVy * dt;
          if (state.playerY <= 0) {
            state.playerY = 0;
            state.playerVy = 0;
            state.grounded = true;
          }
        }

        // Drop any corrupt obstacles so they can't brick the run.
        state.obstacles = state.obstacles.filter((obstacle) => Number.isFinite(obstacle.x));

        state.spawnTimer -= dt;
        if (state.spawnTimer <= 0) {
          const rightmost = state.obstacles.reduce(
            (maxX, obstacle) => Math.max(maxX, obstacle.x + obstacle.width),
            -Infinity
          );
          const gap = getRandomGap(state.speed);
          const baseX = width + 60 + Math.random() * 100;
          const spawnX = Number.isFinite(rightmost) ? Math.max(baseX, rightmost + gap) : baseX;
          state.obstacles.push(createObstacle(spawnX, state.score, state.speed, goal));

          if (progress > 0.4 && Math.random() < 0.12 + (progress - 0.4) * 0.28) {
            const first = state.obstacles[state.obstacles.length - 1];
            const secondX = first.x + first.width + getRandomGap(state.speed);
            state.obstacles.push(createObstacle(secondX, state.score, state.speed, goal));
          }

          state.spawnTimer = 0.45 + Math.random() * 1.35;
        }

        const playerBox = {
          x: PLAYER_X + 6,
          y: height - GROUND_PADDING - PLAYER_HEIGHT - state.playerY + 6,
          w: PLAYER_WIDTH - 12,
          h: PLAYER_HEIGHT - 10,
        };

        let hitObstacle = false;
        const nextObstacles = [];

        for (let i = 0; i < state.obstacles.length; i += 1) {
          const obstacle = state.obstacles[i];
          obstacle.x -= state.speed * dt;

          if (!Number.isFinite(obstacle.x)) continue;

          const hit =
            playerBox.x < obstacle.x + obstacle.width - 4 &&
            playerBox.x + playerBox.w > obstacle.x + 4 &&
            playerBox.y < height - GROUND_PADDING - 2 &&
            playerBox.y + playerBox.h > height - GROUND_PADDING - obstacle.height + 4;

          if (hit) {
            hitObstacle = true;
            break;
          }

          if (obstacle.x + obstacle.width > -20) {
            nextObstacles.push(obstacle);
          }
        }

        if (hitObstacle) {
          hitLockRef.current = true;
          stateRef.current = createInitialState();
          setScore(0);
          onHitRef.current?.();
        } else {
          state.obstacles = nextObstacles;

          if (state.score >= goal && !wonRef.current) {
            wonRef.current = true;
            state.score = goal;
            setScore(goal);
            onWinRef.current?.();
          }
        }
      }

      draw(width, height);
      frameId = window.requestAnimationFrame(step);
    };

    frameId = window.requestAnimationFrame(step);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const goal = Number(targetScore) || TARGET_DEFAULT;

  return (
    <div className="runner-challenge">
      <p className="question-text">Reach {goal}!</p>
      <p className="runner-hint">Tap / click / space to jump</p>
      <button
        type="button"
        className="runner-stage"
        aria-label="Runner game. Press space or tap to jump."
        disabled={locked}
        onClick={() => {
          if (!activeRef.current || lockedRef.current || wonRef.current || hitLockRef.current) {
            return;
          }
          tryJump(stateRef.current);
        }}
      >
        <canvas ref={canvasRef} className="runner-canvas" />
        <span className="runner-score-mobile">
          {score} / {goal}
        </span>
      </button>
    </div>
  );
}

export default RunnerGame;
